import { useCallback } from 'react';
import { NoteType, NotePriority } from '../../../../../types/api';
import { useFormState } from '../../../../../hooks/useFormState';

export interface NoteFormData {
  subject: string;
  content: string;
  note_type: NoteType;
  priority: NotePriority;
  date_of_action: string;
}

const initialFormData: NoteFormData = {
  subject: '',
  content: '',
  note_type: 'general',
  priority: 'information',
  date_of_action: new Date().toISOString().split('T')[0],
};

export const useNoteFormState = (initialData?: Partial<NoteFormData>) => {
  const { formData, errors, isValid, updateField, validateForm, resetForm, setFormData } =
    useFormState<NoteFormData>({
      initialData: {
        ...initialFormData,
        ...initialData,
      },
      validationRules: [
        {
          field: 'subject',
          validate: (value) => Boolean(value?.trim()),
          errorMessage: 'Subject is required',
        },
        {
          field: 'content',
          validate: (value) => Boolean(value?.trim()),
          errorMessage: 'Content is required',
        },
        {
          field: 'date_of_action',
          validate: (value) => Boolean(value),
          errorMessage: 'Date is required',
        },
      ],
    });

  // Helper function to set form data from an existing note
  const setFormFromNote = useCallback(
    (note: {
      subject: string;
      content: string;
      note_type: NoteType;
      priority: NotePriority;
      date_of_action?: string | null;
    }) => {
      setFormData({
        subject: note.subject,
        content: note.content,
        note_type: note.note_type,
        priority: note.priority,
        date_of_action: note.date_of_action || new Date().toISOString().split('T')[0],
      });
    },
    [setFormData]
  );

  // Return the same interface as the original hook for backward compatibility
  return {
    formData,
    errors,
    updateField,
    validateForm,
    resetForm: (newInitialData?: Partial<NoteFormData>) => {
      resetForm({
        ...initialFormData,
        ...newInitialData,
      });
    },
    setFormFromNote,
    isValid,
  };
};
